import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, ShieldAlert, Truck, PackageOpen,
  RefreshCw, Clock,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import api from '../../utils/api';
import StatCard from '../../components/ui/StatCard';
import ActivityBarChart from '../../components/charts/ActivityBarChart';

const activityTypeColor = {
  inward:          'text-blue-600',
  dispatch:        'text-emerald-600',
  quarantine:      'text-zip-red',
  direct_delivery: 'text-purple-600',
};

const activityDot = {
  inward:          'bg-blue-400',
  dispatch:        'bg-emerald-400',
  quarantine:      'bg-zip-red',
  direct_delivery: 'bg-purple-400',
};

const activityVariants = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.05 } },
};
const activityItem = {
  hidden:  { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.2 } },
};

const Skeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="zip-card p-5 h-28 skeleton" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-3 zip-card p-5 h-64 skeleton" />
      <div className="lg:col-span-2 zip-card p-5 h-64 skeleton" />
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary]     = useState(null);
  const [trend, setTrend]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const [sRes, tRes] = await Promise.all([
        api.get('/dashboard/summary'),
        api.get('/dashboard/stock-trend?days=7'),
      ]);
      setSummary(sRes.data.data);
      setTrend(tRes.data.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) return <Skeleton />;
  if (!summary) return (
    <div className="flex items-center justify-center py-24 text-zip-muted">
      Failed to load dashboard data
    </div>
  );

  const sections = [
    { id: 'Storage',    count: summary.stock.byType?.storage ?? 0 },
    { id: 'Quarantine', count: summary.stock.byType?.quarantine ?? 0 },
    { id: 'Oversize',   count: summary.stock.byType?.oversize ?? 0 },
  ];
  const maxSection = Math.max(...sections.map((s) => s.count), 1);

  return (
    <div className="space-y-6">
      {/* Refresh row */}
      <div className="flex items-center justify-end">
        <button
          onClick={fetchData}
          className="flex items-center gap-2 text-xs text-zip-muted hover:text-zip-text transition-colors font-mono"
        >
          <RefreshCw size={12} />
          Updated {formatDistanceToNow(lastRefresh, { addSuffix: true })}
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Stock Items"     value={summary.stock.totalItems}         icon={Package} />
        <StatCard label="Inward This Week"      value={summary.inward.weekCount}          icon={PackageOpen} />
        <StatCard label="Dispatched Today"      value={summary.dispatch.todayCount}       icon={Truck} />
        <StatCard label="Quarantine Pending"    value={summary.quarantine.quarantined}    icon={ShieldAlert} />
      </div>

      {/* Middle row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Activity feed */}
        <div className="lg:col-span-3 zip-card p-5">
          <h3 className="text-sm font-semibold text-zip-text tracking-tight mb-4">Recent Activity</h3>
          <motion.div
            variants={activityVariants}
            initial="hidden"
            animate="visible"
            className="relative pl-5 space-y-4 max-h-64 overflow-y-auto"
          >
            {/* Left red line */}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-zip-red/30" />

            {(summary.recentActivity ?? []).length === 0 && (
              <p className="text-sm text-zip-muted text-center py-8">No recent activity</p>
            )}
            {(summary.recentActivity ?? []).map((act, i) => (
              <motion.div key={i} variants={activityItem} className="flex items-start gap-3">
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${activityDot[act.type] ?? 'bg-zip-muted'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-mono font-medium capitalize ${activityTypeColor[act.type] ?? 'text-zip-muted'}`}>
                    {act.type?.replace(/_/g, ' ')}
                  </div>
                  <div className="text-sm text-zip-text">{act.description}</div>
                  <div className="text-xs text-zip-muted mt-0.5 flex items-center gap-1">
                    <Clock size={10} />
                    {act.user} · {formatDistanceToNow(new Date(act.timestamp), { addSuffix: true })}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Rack occupancy */}
        <div className="lg:col-span-2 zip-card p-5">
          <h3 className="text-sm font-semibold text-zip-text tracking-tight mb-5">Rack Occupancy</h3>
          <div className="space-y-4">
            {sections.map(({ id, count }) => (
              <div key={id}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-mono text-zip-muted">{id}</span>
                  <span className="text-sm font-bold text-zip-text font-mono">{count}</span>
                </div>
                <div className="h-2 rounded-full bg-zip-border overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(count / maxSection) * 100}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
                    className="h-full rounded-full bg-zip-red"
                    style={{ minWidth: count > 0 ? '4px' : '0' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="zip-card p-5">
        <h3 className="text-sm font-semibold text-zip-text tracking-tight mb-4">
          Last 7 Days — Inward vs Outward
        </h3>
        <ActivityBarChart data={trend} />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'New Inward Receipt', path: '/inward/new' },
          { label: 'View Quarantine',    path: '/quarantine' },
          { label: 'New Dispatch',       path: '/dispatch/new' },
          { label: 'Direct Delivery',    path: '/direct-delivery/new' },
        ].map(({ label, path }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className="zip-card px-4 py-3 text-sm font-medium text-zip-muted hover:text-zip-text hover:border-zip-red/40 transition-all duration-200 text-left"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
